import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Printer, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, Surface } from "@/components/ds";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

const STATUS = [
  { value: "ATIVA", label: "Ativa" },
  { value: "MANUTENCAO", label: "Em manutenção" },
  { value: "INATIVA", label: "Inativa" },
];

const EMPTY = {
  name: "",
  manufacturer: "",
  model: "",
  status: "ATIVA",
  power_watts: "",
  cost_per_hour: "",
  preferred: false,
  notes: "",
};

/** Cadastro oficial das impressoras (Configurações). Calculadora e Produção só consultam. */
export function PrintersCard() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api
      .get("/printers")
      .then(({ data }) => setList(data))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(load, [load]);

  const remove = async (p) => {
    try {
      await api.delete(`/printers/${p.id}`);
      toast.success("Impressora removida.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setStatus = async (p, status) => {
    try {
      await api.put(`/printers/${p.id}/status`, { status });
      toast.success("Status atualizado.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Surface className="space-y-4 p-6 lg:col-span-2" data-testid="printers-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg">Impressoras</h2>
          <p className="text-xs text-muted-foreground">
            Potência e custo por hora alimentam a Calculadora de Preço.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="new-printer-btn"
          className="h-10 gap-2 rounded-full px-5 text-xs font-semibold"
        >
          <Plus className="h-4 w-4" /> Nova impressora
        </Button>
      </div>

      {list.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhuma impressora cadastrada ainda.
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {list.map((p) => (
            <div
              key={p.id}
              className="min-w-0 rounded-lg border border-white/10 bg-secondary/40 p-4"
              data-testid={`printer-card-${p.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 truncate text-sm font-medium">
                    <Printer className="h-3.5 w-3.5 shrink-0 text-brand" />
                    {p.name}
                    {p.preferred && (
                      <Star className="h-3.5 w-3.5 shrink-0 fill-primary text-brand" />
                    )}
                  </p>
                  <p className="truncate font-num text-[11px] text-muted-foreground">
                    {p.code} · {p.manufacturer} {p.model}
                  </p>
                </div>
                <span className="shrink-0 rounded-full border border-white/15 px-2 py-0.5 text-[10px]">
                  {STATUS.find((s) => s.value === p.status)?.label}
                </span>
              </div>
              <p className="mt-2 font-num text-xs text-muted-foreground">
                {p.power_watts} W · {brl(p.cost_per_hour)}/h
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(p)}
                  data-testid={`edit-printer-btn-${p.id}`}
                  className="h-8 gap-1.5 rounded-full text-[11px]"
                >
                  <Pencil className="h-3 w-3" /> Editar
                </Button>
                {p.status !== "ATIVA" ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setStatus(p, "ATIVA")}
                    data-testid={`activate-printer-btn-${p.id}`}
                    className="h-8 rounded-full text-[11px]"
                  >
                    Ativar
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setStatus(p, "MANUTENCAO")}
                    data-testid={`maintenance-printer-btn-${p.id}`}
                    className="h-8 rounded-full text-[11px]"
                  >
                    Manutenção
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => remove(p)}
                  data-testid={`delete-printer-btn-${p.id}`}
                  className="h-8 gap-1.5 rounded-full text-[11px] text-destructive"
                >
                  <Trash2 className="h-3 w-3" /> Excluir
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <PrinterModal
          printer={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </Surface>
  );
}

function PrinterModal({ printer, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...printer });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = {
      name: form.name,
      manufacturer: form.manufacturer || "",
      model: form.model || "",
      status: form.status,
      power_watts: Number(form.power_watts || 0),
      cost_per_hour: Number(form.cost_per_hour || 0),
      preferred: !!form.preferred,
      notes: form.notes || "",
    };
    try {
      if (printer.id) await api.put(`/printers/${printer.id}`, body);
      else await api.post("/printers", body);
      toast.success(printer.id ? "Impressora atualizada." : "Impressora cadastrada.");
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={printer.id ? "Editar impressora" : "Nova impressora"}
      description="A identificação interna é gerada automaticamente (PRN-00001)."
      testid="printer-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <Field label="Nome">
          <Input value={form.name} onChange={set("name")} required placeholder="Bambu A1 #01" data-testid="printer-name-input" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Fabricante">
            <Input value={form.manufacturer} onChange={set("manufacturer")} data-testid="printer-manufacturer-input" />
          </Field>
          <Field label="Modelo">
            <Input value={form.model} onChange={set("model")} data-testid="printer-model-input" />
          </Field>
          <Field label="Potência (W)" hint="Usada com a tarifa de energia">
            <Input type="number" min="0" value={form.power_watts} onChange={set("power_watts")} className="font-num" data-testid="printer-power-input" />
          </Field>
          <Field label="Custo operacional (R$/h)" hint="Depreciação, manutenção, desgaste">
            <Input type="number" min="0" step="0.01" value={form.cost_per_hour} onChange={set("cost_per_hour")} className="font-num" data-testid="printer-cost-input" />
          </Field>
        </div>
        <Field label="Status">
          <NativeSelect
            value={form.status}
            onChange={(v) => setForm((f) => ({ ...f, status: v }))}
            options={STATUS}
            data-testid="printer-status-select"
          />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!form.preferred}
            onChange={(e) => setForm((f) => ({ ...f, preferred: e.target.checked }))}
            data-testid="printer-preferred-checkbox"
            className="h-4 w-4 accent-primary"
          />
          Usar como impressora preferencial nos cálculos
        </label>
        <Field label="Observações">
          <Textarea value={form.notes} onChange={set("notes")} rows={2} data-testid="printer-notes-input" />
        </Field>
        <div className="flex justify-end gap-2 border-t border-white/10 pt-4">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-full" data-testid="printer-cancel-btn">
            Cancelar
          </Button>
          <Button type="submit" disabled={busy} className="rounded-full" data-testid="printer-save-btn">
            {busy ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </form>
    </ResponsiveModal>
  );
}
